import React, {useEffect, useState, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Box, Paper, Grid} from '@mui/material';
import {AppDispatch} from '../../../shared/state/store';
import {RootState} from '../../../shared/state/root-reducer';
import {
    clearStatus,
    clearRiskClassSettingsStatus,
} from '../../../admin-configuration/state/admin-configuration-slice';
import {
    clearFacilityRiskThresholdsStatus,
    clearClaimRatesStatus,
    clearClientRiskClassSettingsStatus,
    clearMultiFacilityRiskClassSettingsStatus,
    clearMultiFacilityReferralSourceRevenueStatus,
    clearMultiFacilityReferralSourcesStatus,
} from '../../../shared/state/risk-assessment-slice';
import {
    configGetRiskThresholds,
    configGetLOC,
    configGetRiskClassSettings,
} from '../../../admin-configuration/state/admin-configuration-thunk';
import {
    getMultiFacilityRiskThresholds,
    getClientClaimRates,
    getClientRiskClassSettings,
    getMultiFacilityiskClassSettings,
    getMultiFacilityReferralSourceRevenue,
    getMultiFacilityReferralSources,
} from '../../../shared/state/risk-assessment-thunk';
import {
    MultiFacilityRiskThreshold,
    FacilityRiskClassSettings,
    ReferralSourceRevenue,
} from '../../../estimates/models/risk-assessment';
import {
    getClientDetails,
} from '../../../admissions-advisor/state/estimator-thunk';
import {getClientDeficiencyNotification} from '../../../implementation-specialist/state/clients/implementation-clients-thunk';
import {clearClientDeficiencyNotificationStatus} from '../../../implementation-specialist/state/clients/implementation-clients-slice';
import {clearVobClassificationsError} from '../../../shared/state/vob-slice';
import {loadVobClassifications} from '../../../shared/state/vob-thunk';
import {
    FacilityRiskAssessment, 
    Estimate, 
    Vob
} from '@finpay/estimation-types';
import {useFormik} from 'formik';
import * as Yup from 'yup';
import {MenuItem, InputAdornment, Typography} from '@mui/material';
import {Button, TextField} from '@finpay-development/shared-components';
import {ImplementationFacility} from '../../../implementation-specialist/components/implementation-clients/details/models/implementation-facility';
import {newRiskScoringSchema} from '../../../shared/validation/schemas';
import {
    payorRisk,
    timingRisk,
} from '../../../shared/model/timing-and-payor-risk';
import {saveCallbackStatus} from '@finpay-development/shared-components';

import {
    getClientRiskAssessmentConfigs,
    getFacilityReferralSources,
} from '../../../implementation-specialist/state/clients/implementation-clients-thunk';
import {
    clearClientRiskAssessmentConfigs,
    clearFacilityReferralSourcesStatus,
} from '../../../implementation-specialist/state/clients/implementation-clients-slice';
import {configGetFinPayRiskClasses} from '../../../admin-configuration/state/admin-configuration-thunk';
import {showErrorStatus} from '../../../security/state/user-slice';
import {ClientRiskAssessmentFields} from '../../../shared/model/client-risk-assessment-config';
import RiskAssessmentMidSection from './risk-assessment-midsection';
import {
    saveSelectedEstimate,
    saveSelectedEstimateSelfPay,
} from '../../../shared/state/estimator-thunk';
import {
    getClientLevelsOfCare,
} from '../../../patient/state/patient-thunk';
import RiskAssessmentBottomSection from './risk-assessment-bottomsection';
import {
    useIsDisabledAfterConvert
} from '../../../estimates/hooks/use-is-disabled-after-convert';
import {riskAssessmentUtils} from '../../../estimates/utils/risk-assessment-utils';
import { isSelfPayPayor } from '../../../shared/utils/insurance-utils';

const requiredProperties = [
    'monthlyOperatingCostIp',
    'monthlyOperatingCostOp',
    'bedCapacityIp',
    'bedCapacityOp',
    'variableCostPct',
];

const mapToPrettyPropertyNames = {
    monthlyOperatingCostIp: 'In Patient Total Monthly Operating Costs',
    monthlyOperatingCostOp: 'Out Patient Total Monthly Operating Costs',
    bedCapacityIp: 'In Patient Bed Capacity',
    bedCapacityOp: 'Out Patient Bed Capacity',
    variableCostPct: 'Variable Cost Percentage',
};

const {findInvalidProperties, isValidNumber, generateMessage} =
    riskAssessmentUtils;

export interface RiskAssessmentFormikValues {
    facilityCensus?: number;
    payorRiskId?: number;
    timingRiskId?: number;
    riskClassId?: number;
    facilityReferralSourceId?: number;
}

const RiskAssessmentMain = () => {
    const state = {
        vobPatientState: useSelector(
            (state: RootState) =>
                state.admissionsAdvisorContext?.vobPatientContext.patient
        ),
        vobState: useSelector(
            (state: RootState) => state.vobContext.selectedVob
        ),
        selectedPatient: useSelector(
            (state: RootState) => state.patientContext.selectedPatient
        ),
        selectedPatientCurrentInsurance: useSelector(
            (state: RootState) => state.patientContext.selectedPatientCurrentInsurance
        ),
        payersMap: useSelector(
            (state: RootState) =>state.adminContext.adminConfigurationContext.payersMap
        ),
        configRiskThresholds: useSelector(
            (state: RootState) =>
                state.adminContext.adminConfigurationContext?.riskThresholds
        ),
        isLoadingConfigRiskThresholdsState: useSelector(
            (state: RootState) =>
                state.adminContext.adminConfigurationContext?.isLoading
                    .isLoadingRiskThresholds
        ),
        getConfigRiskThresholdsStatus: useSelector(
            (state: RootState) =>
                state.adminContext.adminConfigurationContext?.isLoading
                    .getRiskThresholdsStatus
        ),
        // holds risk threshold data for all client facilities
        multiFacilityRiskThresholds: useSelector(
            (state: RootState) =>
                state.riskAssessmentContext?.multiFacilityRiskThresholds
        ),
        isLoadingFacilityRiskThresholdsState: useSelector(
            (state: RootState) =>
                state.riskAssessmentContext?.isLoading
                    .isLoadingFacilityRiskThresholds
        ),
        facilityRiskThresholdsStatusState: useSelector(
            (state: RootState) =>
                state.riskAssessmentContext?.isLoading
                    .facilityRiskThresholdsStatus
        ),
        // begin level of care state
        masterLevelsOfCareState: useSelector(
            (state: RootState) =>
                state.adminContext.adminConfigurationContext?.levelsOfCare
        ),
        // clientLevelsOfCareState: useSelector(
        //     (state: RootState) =>
        //         state.estimatorContext
        //             ?.clientLevelsOfCare
        // ),
        // getClientLOCStatus: useSelector(
        //     (state: RootState) =>
        //         state.estimatorContext.isLoading
        //             .getClientLOCStatus
        // ),
        // // levels of care for all client facilities
        // isLoadingClientLoc: useSelector(
        //     (state: RootState) =>
        //         state.estimatorContext.isLoading
        //             .isLoadingClientLoc
        // ),
        // configGetLOCStatus: useSelector(
        //     (state: RootState) =>
        //         state.estimatorContext.isLoading
        //             .configGetLOCStatus
        // ),
        // isLoadingMasterLoc: useSelector(
        //     (state: RootState) =>
        //         state.estimatorContext.isLoading
        //             .isLoadingMasterLoc
        // ),
        // payer claim rates
        clientPayerClaimRatesState: useSelector(
            (state: RootState) =>
                state.riskAssessmentContext?.clientPayerClaimRates
        ),
        isLoadingClientClaimRatesState: useSelector(
            (state: RootState) =>
                state.riskAssessmentContext?.isLoading
                    .isLoadingClientClaimRates
        ),
        clientClaimRatesStatusState: useSelector(
            (state: RootState) =>
                state.riskAssessmentContext?.isLoading
                    .clientClaimRatesStatus
        ),
        // config risk class settings
        configRiskClassSettingsState: useSelector(
            (state: RootState) =>
                state.adminContext.adminConfigurationContext?.riskClassSettings
        ),
        isLoadingConfigRiskClassSettingState: useSelector(
            (state: RootState) =>
                state.adminContext.adminConfigurationContext?.isLoading
                    .isLoadingRiskClassSettings
        ),
        getConfigRiskClassSettingStatus: useSelector(
            (state: RootState) =>
                state.adminContext.adminConfigurationContext?.isLoading
                    .getRiskClassSettingsStatus
        ),
        // client risk class settings
        clientRiskClassSettingsState: useSelector(
            (state: RootState) =>
                state.riskAssessmentContext?.clientRiskClassSettings
        ),
        // clientDetails: useSelector(
        //     (state: RootState) =>
        //         state.estimatorContext?.clientDetails
        // ),
        isLoadingClientRiskClassSettingsState: useSelector(
            (state: RootState) =>
                state.riskAssessmentContext?.isLoading
                    .isLoadingClientRiskClassSettings
        ),
        clientRiskClassSettingsStatusState: useSelector(
            (state: RootState) =>
                state.riskAssessmentContext?.isLoading
                    .clientRiskClassSettingsStatus
        ),
        // facility risk class settings
        multiFacilityRiskClassSettingsState: useSelector(
            (state: RootState) =>
                state.riskAssessmentContext?.multiFacilityRiskClassSettings
        ),
        isLoadingMultiFacilityRiskClassSettingsState: useSelector(
            (state: RootState) =>
                state.riskAssessmentContext?.isLoading
                    .isLoadingMultiFacilityRiskClassSettings
        ),
        multiFacilityRiskClassSettingsStatusState: useSelector(
            (state: RootState) =>
                state.riskAssessmentContext?.isLoading
                    .multiFacilityRiskClassSettingsStatus
        ),
        // client deficiency notofication
        clientDeficiencyNotificationState: useSelector(
            (state: RootState) =>
                state.implementationContext.implementationSpecialistClient
                    .currentClientDeficiencyNotification
        ),
        isLoadingDeficiencyNotificationState: useSelector(
            (state: RootState) =>
                state.implementationContext.implementationSpecialistClient
                    .isLoadingDeficiencyNotification
        ),
        clientDeficiencyNotificationStatusState: useSelector(
            (state: RootState) =>
                state.implementationContext.implementationSpecialistClient
                    .clientDeficiencyNotificationStatus
        ),
        // facility referral sources
        multiFacilityReferralSourcesState: useSelector(
            (state: RootState) =>
                state.riskAssessmentContext?.multiFacilityReferralSources
        ),
        isLoadingMultiFacilityReferralSourcesState: useSelector(
            (state: RootState) =>
                state.riskAssessmentContext?.isLoading
                    .isLoadingMultiFacilityReferralSources
        ),
        multiFacilityReferralSourcesStatusState: useSelector(
            (state: RootState) =>
                state.riskAssessmentContext?.isLoading
                    .multiFacilityReferralSourcesStatus
        ),
        // level of care referral source revenue
        multiFacilityReferralSourceRevenueState: useSelector(
            (state: RootState) =>
                state.riskAssessmentContext?.multiFacilityReferralSourceRevenue
        ),
        isLoadingtMultiFacilityReferralSourceRevenueState: useSelector(
            (state: RootState) =>
                state.riskAssessmentContext?.isLoading
                    .isLoadingtMultiFacilityReferralSourceRevenue
        ),
        multiFacilityReferralSourceRevenueStatusState: useSelector(
            (state: RootState) =>
                state.riskAssessmentContext?.isLoading
                    .multiFacilityReferralSourceRevenueStatus
        ),
        // vob classifications
        vobClassificationsState: useSelector(
            (state: RootState) =>
                state.vobContext.vobClassifications
        ),
        isLoadingVobClassificationsState: useSelector(
            (state: RootState) =>
                state.vobContext.isLoading.vobClassifications
        ),
        vobClassificationsLoadingStatusState: useSelector(
            (state: RootState) =>
                state.vobContext.error.vobClassifications === null
        ),
        estimate: useSelector((state: RootState) => {
            return state.estimatorContext.selectedEstimate;
        }),
        rateAsPayorPlan: useSelector((state: RootState) => {
            return state.estimatorContext.rateAsPayorPlan;
        }),
        isLoadingFacilities: useSelector(
            (state: RootState) =>
                state.implementationContext.implementationFacility.isLoading
        ),
        patientEpisodeMarginSettings: useSelector(
            (state: RootState) =>
                state.riskAssessmentContext
                    .patientEpisodeMarginSettings
        ),
        patientEpisodeMarginSettingsStatus: useSelector(
            (state: RootState) =>
                state.riskAssessmentContext.isLoading
                    .patientEpisodeMarginSettingsStatus
        ),
        riskAssessmentFormState: useSelector(
            (state: RootState) =>
                state.riskAssessmentContext
                    .riskAssessmentForm
        ),
        facilityReferralSources: useSelector(
            (state: RootState) =>
                state.implementationContext.implementationSpecialistClient
                    ?.facilityReferralSources
        ),
        isLoadingFacilityReferralSources: useSelector(
            (state: RootState) =>
                state.implementationContext.implementationSpecialistClient
                    ?.isLoadingFacilityReferralSources
        ),
        facilityReferralSourcesStatus: useSelector(
            (state: RootState) =>
                state.implementationContext.implementationSpecialistClient
                    ?.facilityReferralSourcesStatus
        ),
        finPayRiskClasses: useSelector(
            (state: RootState) =>
                state.adminContext.adminConfigurationContext?.finPayRiskClasses
        ),
        clientRiskAssessmentConfigs: useSelector(
            (state: RootState) =>
                state.implementationContext.implementationSpecialistClient
                    ?.clientRiskAssessmentConfigs
        ),
        isLoadingClientRiskAssessmentConfigs: useSelector(
            (state: RootState) =>
                state.implementationContext.implementationSpecialistClient
                    ?.isLoadingClientRiskAssessmentConfigs
        ),
        clientRiskAssessmentConfigLoadingStatus: useSelector(
            (state: RootState) =>
                state.implementationContext.implementationSpecialistClient
                    ?.clientRiskAssessmentConfigLoadingStatus
        ),
        facilities: useSelector(
            (state: RootState) => {
                const allClientsMap = state.implementationContext?.implementationSpecialistClient.allClientsMap;
                const estimate = state.estimatorContext.selectedEstimate;
                const vobState = state.vobContext.selectedVob;
                const currentClientId = estimate?.clientId || vobState?.client?.clientId;
                
                if (currentClientId && allClientsMap) {
                    const targetClient = allClientsMap.get(Number(currentClientId));
                    return targetClient?.clientFacilities || [];
                }
                return [];
            }
        ) as ImplementationFacility[]
    };

    const {
        facilities,
        vobState,
        vobPatientState,
        selectedPatient,
        selectedPatientCurrentInsurance,
        payersMap,
        // clientDetails,
        facilityReferralSources,
        isLoadingFacilityReferralSources,
        facilityReferralSourcesStatus,
        finPayRiskClasses,
        clientRiskAssessmentConfigs,
        clientRiskAssessmentConfigLoadingStatus,
        isLoadingClientRiskAssessmentConfigs,
        estimate,
        rateAsPayorPlan,
        configRiskThresholds,
        isLoadingConfigRiskThresholdsState,
        getConfigRiskThresholdsStatus,
        multiFacilityRiskThresholds,
        isLoadingFacilityRiskThresholdsState,
        facilityRiskThresholdsStatusState,
        // isLoadingMasterLoc,
        // configGetLOCStatus,
        masterLevelsOfCareState,
        // isLoadingClientLoc,
        clientPayerClaimRatesState,
        // getClientLOCStatus,
        isLoadingClientClaimRatesState,
        clientClaimRatesStatusState,
        configRiskClassSettingsState,
        isLoadingConfigRiskClassSettingState,
        getConfigRiskClassSettingStatus,
        clientRiskClassSettingsState,
        isLoadingClientRiskClassSettingsState,
        clientRiskClassSettingsStatusState,
        multiFacilityRiskClassSettingsState,
        isLoadingMultiFacilityRiskClassSettingsState,
        multiFacilityRiskClassSettingsStatusState,
        clientDeficiencyNotificationState,
        isLoadingDeficiencyNotificationState,
        clientDeficiencyNotificationStatusState,
        multiFacilityReferralSourcesState,
        isLoadingMultiFacilityReferralSourcesState,
        multiFacilityReferralSourcesStatusState,
        multiFacilityReferralSourceRevenueState,
        isLoadingtMultiFacilityReferralSourceRevenueState,
        multiFacilityReferralSourceRevenueStatusState,
        vobClassificationsState,
        isLoadingVobClassificationsState,
        vobClassificationsLoadingStatusState
    } = state;

    const dispatch = useDispatch<AppDispatch>();

    const isDisabledAfterConvert = useIsDisabledAfterConvert()

    const [enableRunButton, setEnableRunButton] = useState(false);

    const [clientLocIsLoaded, setClientLocIsLoaded] = useState<Boolean>(false);
    const [claimRatesIsLoaded, setClaimRatesIsLoaded] = useState(false);
    const [
        configRiskClassSettingsIsLoaded,
        setConfigRiskClassSettingsIsLoaded,
    ] = useState(false);

    const [
        clientRiskClassSettingsIsLoaded,
        setClientRiskClassSettingsIsLoaded,
    ] = useState(false);

    const [
        multiFacilityRiskClassSettingsIsLoaded,
        setMultiFacilityRiskClassSettingsIsLoaded,
    ] = useState(false);

    const [
        clientDeficiencyNotificationIsLoaded,
        setClientDeficiencyNotificationIsLoaded,
    ] = useState(false);

    const [
        multiFacilityReferralSourcesAreLoaded,
        setMultiFacilityReferralSourcesAreLoaded,
    ] = useState(false);

    const [
        multiFacilityReferralSourceRevenueIsLoaded,
        setMultiFacilityReferralSourceRevenueIsLoaded,
    ] = useState(false);

    const [
        patientEpisodeMarginSettingsStateIsLoaded,
        setPatientEpisodeMarginSettingsStateIsLoaded,
    ] = useState(false);

    const [vobClassificationsAreLoaded, setVobClassificationsAreLoaded] =
        useState(false);

    // determine if there are any eiv or npr deficiencies (hides/shows data)
    const deficiencySettingEiv = Boolean(clientDeficiencyNotificationState?.isEiv);
    const hasEivdeficiencies = Boolean(estimate?.riskAssessment?.eivDeficiency);
    const hasNprdeficiencies = Boolean(estimate?.riskAssessment?.netPatientRevenueDeficiency);

    const paramId = -2;

    const allFacilityIds = useMemo(() => {
        return facilities?.flat().map(facility => {
            return facility.facilityId;
        });
    }, [facilities]);

    // const isClientConfiguredForRiskScoring = clientDetails?.raVersion === 2;

    const currentFacility = useMemo(() => {
        return facilities.flat().find((facility: ImplementationFacility) => {
            return facility.facilityId === vobState?.facility?.facilityId;
        });
    }, [facilities, vobState?.facility?.facilityId]);

    const hasReferralSourcesConfigured = facilityReferralSources.length > 0;

    const selectedFacility = useMemo(() => {
        const facilityId = vobState?.facility?.facilityId;

        return facilities?.flat().find(fac => fac?.facilityId === facilityId);
    }, [facilities, vobState?.facility?.facilityId]);

    const isConfigRiskThresholdLoaded =
        !isLoadingConfigRiskThresholdsState &&
        getConfigRiskThresholdsStatus === saveCallbackStatus.success;

    const isMultiFacilityRiskThresholdsLoaded =
        !isLoadingFacilityRiskThresholdsState &&
        facilityRiskThresholdsStatusState === saveCallbackStatus.success;

    // Check if patient has self-pay insurance
    const isSelfPay = useMemo(() => {
        return isSelfPayPayor(selectedPatientCurrentInsurance?.payorId, payersMap);
    }, [selectedPatientCurrentInsurance?.payorId, payersMap]);

    const areClientRiskAssessmentConfigsLoaded = useMemo(() => {
        return (
            clientRiskAssessmentConfigLoadingStatus ===
                saveCallbackStatus.success &&
            !isLoadingClientRiskAssessmentConfigs
        );
    }, [
        clientRiskAssessmentConfigLoadingStatus,
        isLoadingClientRiskAssessmentConfigs,
    ]);

    const selectedFacilityRiskAssessmentConfig = useMemo(() => {
        if (selectedFacility && areClientRiskAssessmentConfigsLoaded) {
            let facilityRiskAssessmentFields:
                | ClientRiskAssessmentFields
                | undefined = clientRiskAssessmentConfigs.find(
                (riskConfig: ClientRiskAssessmentFields) => {
                    return (
                        riskConfig.clientFacilityId ===
                        selectedFacility.facilityId
                    );
                }
            );

            if (!facilityRiskAssessmentFields) {
                facilityRiskAssessmentFields =
                    clientRiskAssessmentConfigs?.find(
                        (riskConfig: ClientRiskAssessmentFields) => {
                            return riskConfig.clientFacilityId === null;
                        }
                    )!;
            }

            return facilityRiskAssessmentFields;
        }
    }, [selectedFacility, areClientRiskAssessmentConfigsLoaded]);

    const [listOfMissingOrInvalidProperties, isFacilityConfigured] =
        useMemo(() => {
            const {
                variableCostPct,
                monthlyOperatingCostIp,
                monthlyOperatingCostOp,
                bedCapacityIp,
                bedCapacityOp,
            } = selectedFacilityRiskAssessmentConfig || {};

            const propertiesToCheck = {
                monthlyOperatingCostIp,
                monthlyOperatingCostOp,
                bedCapacityIp,
                bedCapacityOp,
                variableCostPct,
            };

            const [listOfMissingOrInvalidProperties, isMissingProperties] =
                findInvalidProperties(
                    propertiesToCheck,
                    requiredProperties,
                    isValidNumber
                );
            if (isMissingProperties) {
                // TODO: return [listOfMissingOrInvalidProperties, false];
                return [[], true];
            } else {
                return [[], true];
            }
        }, [selectedFacilityRiskAssessmentConfig]);

    const isRunButtonEnabled = isFacilityConfigured && enableRunButton && !isDisabledAfterConvert

    const getLevelsOfCareByClient = () => {
        if (masterLevelsOfCareState?.length > 0) {
            const config: any = {
                paramId: paramId,
                clientId: estimate?.clientId as number,
                payerPlanId: vobState?.plan?.payorPlanId,
                filterByPayerPlanId: false,
                masterListLevelsOfCare: masterLevelsOfCareState,
            };
            dispatch(getClientLevelsOfCare(config));
        }
    };

    const riskThresholdData = useMemo(() => {
        if (
            isConfigRiskThresholdLoaded &&
            isMultiFacilityRiskThresholdsLoaded &&
            configRiskThresholds.length > 0
        ) {
            return multiFacilityRiskThresholds.map(
                (multiFacility: MultiFacilityRiskThreshold) => {
                    const mergedRiskThresholds =
                        riskAssessmentUtils.mergeConfigFacilityRiskThresholds(
                            configRiskThresholds,
                            multiFacility.facilityRiskThresholds,
                            multiFacility.clientFacilityId
                        );
                    return {
                        ...multiFacility,
                        facilityRiskThresholds: mergedRiskThresholds,
                    };
                }
            );
        } else {
            return [];
        }
    }, [isConfigRiskThresholdLoaded, isMultiFacilityRiskThresholdsLoaded]);

    useEffect(() => {
        dispatch(configGetFinPayRiskClasses({paramId: paramId}));
    }, []);

    useEffect(() => {
        if(finPayRiskClasses.length > 0) {
            dispatch(configGetRiskClassSettings({paramId: paramId}));
        }
    }, [finPayRiskClasses])

    useEffect(() => {
        return () => {
            dispatch(clearStatus());
            dispatch(clearFacilityRiskThresholdsStatus());
        };
    }, []);

    useEffect(() => {
        if (!masterLevelsOfCareState.length) {
            dispatch(configGetLOC(paramId));
        } else {
            getLevelsOfCareByClient();
        }

        dispatch(loadVobClassifications(paramId));
        dispatch(configGetRiskThresholds({paramId: paramId}));
        dispatch(
            getClientDetails({
                paramId: paramId,
                clientId: estimate?.clientId! as number,
            })
        );

        dispatch(
            getClientRiskClassSettings({
                paramId: paramId,
                clientId: estimate?.clientId as number,
            })
        );

        dispatch(
            getMultiFacilityiskClassSettings({
                paramId: paramId,
                facilityIds: allFacilityIds,
            })
        );

        dispatch(
            getClientClaimRates({
                paramId: paramId,
                clientId: estimate?.clientId as number,
            })
        );

        dispatch(
            getClientDeficiencyNotification(estimate?.clientId as number)
        );

        dispatch(
            getMultiFacilityReferralSources({
                paramId: paramId,
                clientId: estimate?.clientId as number,
                facilityIds: allFacilityIds,
            })
        );

        dispatch(
            getMultiFacilityReferralSourceRevenue({
                paramId: paramId,
                facilityIds: allFacilityIds,
            })
        );

        if (allFacilityIds.length > 0) {
            dispatch(
                getMultiFacilityRiskThresholds({
                    paramId: paramId,
                    facilityIds: allFacilityIds,
                })
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (estimate!.clientId) {
            dispatch(
                getClientRiskAssessmentConfigs({
                    clientId: estimate?.clientId as number,
                })
            );
        }
        
        return () => {
            dispatch(clearClientRiskAssessmentConfigs());
        };
    }, [estimate?.clientId]);
    
    useEffect(() => {
        if (
            !isLoadingFacilityReferralSources &&
            facilityReferralSourcesStatus === saveCallbackStatus.success
        ) {
            // facility referral sources has loaded
            dispatch(clearFacilityReferralSourcesStatus());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingFacilityReferralSources]);
    
    useEffect(() => {
        if (selectedFacility) {
            dispatch(
                getFacilityReferralSources({
                    facilityId: selectedFacility.facilityId,
                    clientId: estimate?.clientId! as number,
                })
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFacility]);
    
    useEffect(() => {
        if (
            clientRiskAssessmentConfigLoadingStatus ===
            saveCallbackStatus.success &&
            !isFacilityConfigured &&
            listOfMissingOrInvalidProperties.length > 0
        ) {
            const listOfMissingProperties = generateMessage(
                listOfMissingOrInvalidProperties,
                mapToPrettyPropertyNames
            );
            const message = `The following required facility settings are missing: ${listOfMissingProperties}. Please contact your system, implementation, or support coordinator.`;
            dispatch(showErrorStatus(message));
        }
    }, [
        selectedFacilityRiskAssessmentConfig,
        isFacilityConfigured,
        clientRiskAssessmentConfigLoadingStatus,
    ]);

    // useEffect(() => {
    //     if (!isLoadingMasterLoc && configGetLOCStatus === apiStatus.success) {
    //         // config Levels of Care has loaded
    //         dispatch(clearConfigGetLOCStatus());
    //         getLevelsOfCareByClient();
    //     }
    //
    //     if (!isLoadingClientLoc && getClientLOCStatus === apiStatus.success) {
    //         // client Levels of Care has loaded
    //         dispatch(clearClientLocStatus());
    //         setClientLocIsLoaded(true);
    //     }
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [isLoadingMasterLoc, isLoadingClientLoc]);

    useEffect(() => {
        // claimrates is done loading
        if (
            !isLoadingClientClaimRatesState &&
            clientClaimRatesStatusState === saveCallbackStatus.success
        ) {
            setClaimRatesIsLoaded(true);
            dispatch(clearClaimRatesStatus());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientClaimRatesStatusState]);

    useEffect(() => {
        // config risk class settings are done loading
        if (
            !isLoadingConfigRiskClassSettingState &&
            getConfigRiskClassSettingStatus === saveCallbackStatus.success
        ) {
            if (configRiskClassSettingsState !== undefined) {
                setConfigRiskClassSettingsIsLoaded(true);
                dispatch(clearRiskClassSettingsStatus());
            }
        }
        // client risk class settings are done loading
        if (
            !isLoadingClientRiskClassSettingsState &&
            clientRiskClassSettingsStatusState === saveCallbackStatus.success
        ) {
            if (clientRiskClassSettingsState !== undefined) {
                setClientRiskClassSettingsIsLoaded(true);
                dispatch(clearClientRiskClassSettingsStatus());
            }
        }
        // facility risk class settings are done loading
        if (
            !isLoadingMultiFacilityRiskClassSettingsState &&
            multiFacilityRiskClassSettingsStatusState ===
                saveCallbackStatus.success
        ) {
            if (multiFacilityRiskClassSettingsState !== undefined) {
                setMultiFacilityRiskClassSettingsIsLoaded(true);
                dispatch(clearMultiFacilityRiskClassSettingsStatus());
            }
        }

        // client deficiency settings are done loading
        if (
            !isLoadingDeficiencyNotificationState &&
            clientDeficiencyNotificationStatusState ===
                saveCallbackStatus.success
        ) {
            if (clientDeficiencyNotificationState !== undefined) {
                setClientDeficiencyNotificationIsLoaded(true);
                dispatch(clearClientDeficiencyNotificationStatus());
            }
        }

        // referral source revenue settings are done loading
        if (
            !isLoadingMultiFacilityReferralSourcesState &&
            multiFacilityReferralSourcesStatusState ===
                saveCallbackStatus.success
        ) {
            if (multiFacilityReferralSourcesState !== undefined) {
                setMultiFacilityReferralSourcesAreLoaded(true);
                dispatch(clearMultiFacilityReferralSourcesStatus());
            }
        }
        if (
            !isLoadingtMultiFacilityReferralSourceRevenueState &&
            multiFacilityReferralSourceRevenueStatusState ===
                saveCallbackStatus.success
        ) {
            if (multiFacilityReferralSourceRevenueState !== undefined) {
                setMultiFacilityReferralSourceRevenueIsLoaded(true);
                dispatch(clearMultiFacilityReferralSourceRevenueStatus());
            }
        }
        // vob classifications
        if (
            !isLoadingVobClassificationsState &&
            vobClassificationsState.length > 0
        ) {
            dispatch(clearVobClassificationsError());
            setVobClassificationsAreLoaded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isLoadingConfigRiskClassSettingState,
        isLoadingClientRiskClassSettingsState,
        isLoadingMultiFacilityRiskClassSettingsState,
        isLoadingDeficiencyNotificationState,
        isLoadingMultiFacilityReferralSourcesState,
        isLoadingtMultiFacilityReferralSourceRevenueState,
        isLoadingVobClassificationsState,
    ]);

    const riskScoringValidationSchema = Yup.object(newRiskScoringSchema);

    function checkIsFormValid(value: {}) {
        riskScoringValidationSchema
            .validate(value, {context: {hasReferralSourcesConfigured}})
            .then(() => {
                setEnableRunButton(true);
            })
            .catch(err => {
                setEnableRunButton(false);
            });
    }

    const riskAssessmentFormik = useFormik<RiskAssessmentFormikValues>({
        initialValues: {
            facilityCensus: estimate?.riskAssessment?.facilityCensus ?? 60,
            payorRiskId: estimate?.riskAssessment?.payorRiskId ?? 1,
            timingRiskId: estimate?.riskAssessment?.timingRiskId ?? 1,
            riskClassId: estimate?.riskAssessment?.riskClassId ?? 1,
            facilityReferralSourceId:
                estimate?.riskAssessment?.facilityReferralSourceId ?? -1,
        },
        validate: checkIsFormValid,
        validationSchema: riskScoringValidationSchema,
        validateOnMount: true,
        enableReinitialize: true,
        onSubmit: () => {},
    });

    const handleButtonClick = async () => {
        let mergedRiskClassSettings: FacilityRiskClassSettings[] =
            riskAssessmentUtils.mergeRiskClassSettings(
                finPayRiskClasses,
                configRiskClassSettingsState,
                clientRiskClassSettingsState,
                multiFacilityRiskClassSettingsState
            );

        // this merges all Referral Sources data for all facilities for lookup
        let mergedReferralSourceData: ReferralSourceRevenue[] =
            riskAssessmentUtils.mergeReferralSourceRevenue(
                multiFacilityReferralSourcesState,
                multiFacilityReferralSourceRevenueState
            );

        // Convert from old Vob format to @finpay/estimation-types Vob format
        // Create a default VOB if vobState is null
        const vobCopy: Vob = (vobState && vobState.vobId) ? {
            vobId: vobState.vobId,
            patientId: vobState.patientId || 0,
            client: {
                clientId: vobState.client?.clientId || 0,
                clientName: vobState.client?.clientName || '',
            },
            facility: {
                facilityId: vobState.facility?.facilityId || 0,
                facilityName: vobState.facility?.facilityName || '',
            },
            payor: {
                payorId: vobState.payor?.payorId,
                payorName: vobState.payor?.payorName || '',
                payorCode: vobState.payor?.payorCode || '',
                inNetwork: vobState.payor?.inNetwork || false,
            },
            plan: {
                payorPlanId: vobState.plan?.payorPlanId,
                planName: vobState.plan?.planName || '',
                planCode: vobState.plan?.planCode || '',
                facilityPlanId: vobState.plan?.facilityPlanId,
            },
            vobSourceId: vobState.vobSourceId || 3, // Default to CRM
            isValid: vobState.isValid || false,
            digitalVerificationMethod: vobState.digitalVerificationMethod || false,
            groupNum: vobState.groupNum || '',
            policyNum: vobState.policyNum || '',
            liveVOB: vobState.liveVOB || false,
            activePolicy: vobState.activePolicy || false,
            isCarryover: vobState.isCarryover || false,
            isCopayReset: vobState.isCopayReset || false,
            policyBeginDate: vobState.policyBeginDate || '',
            policyEndDate: vobState.policyEndDate || '',
            innDeductible: vobState.innDeductible ? {
                total: vobState.innDeductible.total || 0,
                met: vobState.innDeductible.met || 0,
                remaining: vobState.innDeductible.remaining || 0
            } : undefined,
            innOopm: vobState.innOopm ? {
                total: vobState.innOopm.total || 0,
                met: vobState.innOopm.met || 0,
                remaining: vobState.innOopm.remaining || 0,
                isDeductibleIncluded: vobState.innOopm.isDeductibleIncluded || false
            } : undefined,
            innFamilyDeductible: vobState.innFamilyDeductible ? {
                total: vobState.innFamilyDeductible.total || 0,
                met: vobState.innFamilyDeductible.met || 0,
                remaining: vobState.innFamilyDeductible.remaining || 0
            } : undefined,
            innFamilyOopm: vobState.innFamilyOopm ? {
                total: vobState.innFamilyOopm.total || 0,
                met: vobState.innFamilyOopm.met || 0,
                remaining: vobState.innFamilyOopm.remaining || 0,
                isDeductibleIncluded: vobState.innFamilyOopm.isDeductibleIncluded || false
            } : undefined,
            innClassifications: vobState.innClassifications || [], 
            oonDeductible: vobState.oonDeductible ? {
                total: vobState.oonDeductible.total || 0,
                met: vobState.oonDeductible.met || 0,
                remaining: vobState.oonDeductible.remaining || 0
            } : undefined,
            oonOopm: vobState.oonOopm ? {
                total: vobState.oonOopm.total || 0,
                met: vobState.oonOopm.met || 0,
                remaining: vobState.oonOopm.remaining || 0,
                isDeductibleIncluded: vobState.oonOopm.isDeductibleIncluded || false
            } : undefined,
            oonFamilyDeductible: vobState.oonFamilyDeductible ? {
                total: vobState.oonFamilyDeductible.total || 0,
                met: vobState.oonFamilyDeductible.met || 0,
                remaining: vobState.oonFamilyDeductible.remaining || 0
            } : undefined,
            oonFamilyOopm: vobState.oonFamilyOopm ? {
                total: vobState.oonFamilyOopm.total || 0,
                met: vobState.oonFamilyOopm.met || 0,
                remaining: vobState.oonFamilyOopm.remaining || 0,
                isDeductibleIncluded: vobState.oonFamilyOopm.isDeductibleIncluded || false
            } : undefined,
            oonClassifications: vobState.oonClassifications || [],
            notes: vobState.notes || [],
            employer: vobState.employer,
            sourceJson: undefined,
        } : {
            vobId: 0,
            patientId: selectedPatient?.patientId || 0,
            client: {
                clientId: Number(estimate?.clientId) || 0,
                clientName: '',
            },
            facility: {
                facilityId: Number(estimate?.facilityId) || 0,
                facilityName: '',
            },
            payor: {
                payorId: 0,
                payorName: '',
                payorCode: '',
                inNetwork: false,
            },
            plan: {
                payorPlanId: 0,
                planName: '',
                planCode: '',
                facilityPlanId: 0,
            },
            vobSourceId: 3, // Default to CRM
            isValid: false,
            digitalVerificationMethod: false,
            groupNum: '',
            policyNum: '',
            liveVOB: false,
            activePolicy: false,
            isCarryover: false,
            isCopayReset: false,
            policyBeginDate: '',
            policyEndDate: '',
            innClassifications: [],
            oonClassifications: [],
            notes: [],
            sourceJson: undefined,
        };

        // Check if facility is defined
        if (!currentFacility) {
            dispatch(showErrorStatus("Facility not found. Please select a valid facility."));
        }

        const multiFacilityRiskAssessments: FacilityRiskAssessment[] =
            riskAssessmentUtils.calculateFacilityRiskAssessmentsWithEstimate(
                currentFacility!,
                riskAssessmentFormik,
                vobCopy,
                estimate!,
                riskThresholdData,
                mergedRiskClassSettings,
                clientPayerClaimRatesState,
                clientRiskAssessmentConfigs,
                mergedReferralSourceData
            );

        const [facilityRiskAssessment, facilityRiskAssessmentHre] =
            multiFacilityRiskAssessments;

        const {
            priorCareSummary,
            summary,
            crossOverSummary,
            estimateId,
            createUserId,
            createDt,
            lastUpdateUserId,
            lastUpdateDt,
            summarySelection,
            ...estimatePutBody
        } = estimate!;

        const riskAssessment = {
            ...facilityRiskAssessment,
            ...riskAssessmentFormik.values,
            // Ensure facilityName is set
            facilityName: facilityRiskAssessment.facilityName || vobCopy.facility?.facilityName || currentFacility?.facilityName || 'Unknown Facility',
        };

        const riskAssessmentHre = {
            ...facilityRiskAssessmentHre,
            ...riskAssessmentFormik.values,
            // Ensure facilityName is set
            facilityName: facilityRiskAssessmentHre.facilityName || vobCopy.facility?.facilityName || currentFacility?.facilityName || 'Unknown Facility',
        };

        // @ts-ignore
        const putBody: Estimate = {
            ...estimate,
            riskAssessment,
            riskAssessmentHre,
            // Only include rateAsPayorPlan if it's not null
            ...(rateAsPayorPlan ? { rateAsPayorPlan } : {}),
        };

        if (facilityRiskAssessment && facilityRiskAssessmentHre) {
            dispatch(
                (isSelfPay || estimate?.isPrivatePay)
                    ? saveSelectedEstimateSelfPay({ estimateId, estimate: putBody })
                    : saveSelectedEstimate({ estimateId, estimate: putBody })
            ); 
        }
    };

    const riskAssessment = estimate?.riskAssessment;
    const riskAssessmentHre = estimate?.riskAssessmentHre;

    return (
        <>
            <Paper>
                <Box className="m-2 p-2">
                    <Typography variant="subtitle2" className="my-4">
                        Risk Scoring: {currentFacility?.facilityName}
                    </Typography>

                    <Grid
                        container
                        direction="row"
                        spacing={2}
                        className="mb-4"
                    >
                        <Grid item md={2} sm={2} xs={2}>
                            <TextField
                                label="Census"
                                name="facilityCensus"
                                type="number"
                                value={
                                    riskAssessmentFormik.values.facilityCensus
                                }
                                endAdornment={
                                    <InputAdornment position="end">
                                        <span>
                                            <strong>%</strong>
                                        </span>
                                    </InputAdornment>
                                }
                                error={
                                    riskAssessmentFormik.touched[
                                        'facilityCensus'
                                    ] &&
                                    riskAssessmentFormik.errors[
                                        'facilityCensus'
                                    ]
                                        ? riskAssessmentFormik.errors[
                                              'facilityCensus'
                                          ]
                                        : ''
                                }
                                onChange={(e: Event) => {
                                    riskAssessmentFormik.handleChange(e);
                                }}
                                test-id="facility-census"
                                onBlur={riskAssessmentFormik.handleBlur}
                            />
                        </Grid>

                        <Grid item md={2} sm={2}>
                            <TextField
                                select
                                error={
                                    riskAssessmentFormik.touched.payorRiskId &&
                                    Boolean(
                                        riskAssessmentFormik.errors.payorRiskId
                                            ? riskAssessmentFormik.errors
                                                  .payorRiskId
                                            : ''
                                    )
                                }
                                label="Payer Risk"
                                className="state-field"
                                name="payorRiskId"
                                value={riskAssessmentFormik.values.payorRiskId}
                                onChange={(e: Event) => {
                                    riskAssessmentFormik.handleChange(e);
                                }}
                                onBlur={riskAssessmentFormik.handleBlur}
                                placeholder="Select Payer Risk"
                            >
                                {payorRisk.map(risk => (
                                    <MenuItem
                                        key={risk.payorRiskId}
                                        value={risk.payorRiskId}
                                    >
                                        {risk.payorRiskName}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item md={2} sm={2}>
                            <TextField
                                select
                                error={
                                    riskAssessmentFormik.touched.timingRiskId &&
                                    Boolean(
                                        riskAssessmentFormik.errors.timingRiskId
                                            ? riskAssessmentFormik.errors
                                                  .timingRiskId
                                            : ''
                                    )
                                }
                                label="Timing Risk"
                                className="state-field"
                                name="timingRiskId"
                                value={riskAssessmentFormik.values.timingRiskId}
                                onChange={(e: Event) => {
                                    riskAssessmentFormik.handleChange(e);
                                }}
                                onBlur={riskAssessmentFormik.handleBlur}
                            >
                                {timingRisk.map(risk => {
                                    return (
                                        <MenuItem
                                            key={risk.timingRiskId}
                                            value={risk.timingRiskId}
                                        >
                                            {risk.timingRiskName}
                                        </MenuItem>
                                    );
                                })}
                            </TextField>
                        </Grid>
                        <Grid item md={4} sm={4}>
                            <TextField
                                select
                                label="Referral Source"
                                className="state-field"
                                name="facilityReferralSourceId"
                                test-id="risk-assessment-referral-source"
                                value={
                                    riskAssessmentFormik.values
                                        .facilityReferralSourceId
                                }
                                onChange={(e: Event) => {
                                    riskAssessmentFormik.handleChange(e);
                                }}
                                disabled={!hasReferralSourcesConfigured}
                                onBlur={riskAssessmentFormik.handleBlur}
                                error={Boolean(
                                    riskAssessmentFormik.touched
                                        .facilityReferralSourceId &&
                                        riskAssessmentFormik.errors
                                            .facilityReferralSourceId
                                )}
                            >
                                {!hasReferralSourcesConfigured && (
                                    <MenuItem value={-1}>
                                        Data Not Provided
                                    </MenuItem>
                                )}
                                {hasReferralSourcesConfigured &&
                                    facilityReferralSources.map(refsrc => (
                                        <MenuItem
                                            key={
                                                refsrc.facilityReferralSourceId
                                            }
                                            value={
                                                refsrc.facilityReferralSourceId
                                            }
                                            test-id={`risk-assessment-risk-class-option-${refsrc.facilityReferralSourceId}`}
                                        >
                                            {refsrc.facilityReferralSourceName}
                                        </MenuItem>
                                    ))}
                            </TextField>
                        </Grid>
                    </Grid>
                    <Grid container>
                        <Grid item md={4} sm={4}>
                            <TextField
                                select
                                label="Risk Class"
                                className="state-field"
                                name="riskClassId"
                                test-id="risk-assessment-risk-class"
                                value={riskAssessmentFormik.values.riskClassId}
                                onChange={(e: Event) => {
                                    riskAssessmentFormik.handleChange(e);
                                }}
                                onBlur={riskAssessmentFormik.handleBlur}
                                error={Boolean(
                                    riskAssessmentFormik.touched.riskClassId &&
                                        riskAssessmentFormik.errors.riskClassId
                                )}
                            >
                                {finPayRiskClasses.map(riskClass => (
                                    <MenuItem
                                        key={riskClass.riskClassId}
                                        value={riskClass.riskClassId}
                                        test-id={`risk-assessment-risk-class-option-${riskClass.riskClassId}`}
                                    >
                                        {riskClass.riskClassName}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        {/* blank for spacing */}
                        <Grid item md={4} sm={4}></Grid>
                        <Grid
                            item
                            md={4}
                            sm={4}
                            style={{textAlign: 'right', marginBottom: '1em'}}
                        >
                            <Button
                                onClick={() => handleButtonClick()}
                                disabled={!isRunButtonEnabled}
                            >
                                Run Assessment
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid
                        container
                        direction="row"
                        spacing={2}
                        className="mt-2"
                    ></Grid>
                </Box>
            </Paper>

            {/* Risk Assessment Grid */}
            {riskAssessmentFormik.isValid &&
                isFacilityConfigured &&
                riskAssessment &&
                riskAssessmentHre && (
                    <>
                        <RiskAssessmentMidSection
                            riskAssessment={estimate?.riskAssessment}
                            riskAssessmentHre={estimate?.riskAssessmentHre}
                            deficiencySettingEiv={deficiencySettingEiv}
                            hasEivdeficiencies={hasEivdeficiencies}
                            hasNprdeficiencies={hasNprdeficiencies}
                            // isClientConfiguredForRiskScoring={
                            //     isClientConfiguredForRiskScoring
                            // }
                            isClientConfiguredForRiskScoring={
                                true
                            }
                        />

                        <RiskAssessmentBottomSection />

                        {/* <EstFooter /> */}
                    </>
                )}
        </>
    );
};

export default RiskAssessmentMain;
